import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Patch, Post } from "@repo/utils/api";
import Chainable from "@repo/utils/Chainable";
import Compare from "@repo/utils/Compare";
import UArray from "@repo/utils/UArray";
import UObject from "@repo/utils/UObject";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Organization from "@repo/vygor-models/Organization";

import { api } from "..";

import { Bookable, useBookables } from "./bookables";
import { Booking, useBookings } from "./bookings";
import { Membership, useMemberships } from "./memberships";
import { Product, useProducts } from "./products";
import { Recurrence, useRecurrences } from "./recurrences";
import { Staff, useStaffs } from "./staffs";
import { useWaivers, Waiver } from "./waivers";

export { Organization };

export type OrganizationsRest = Get<
  { forUser?: boolean },
  { organizations: Organization.FromApi[] }
> & {
  [id: Organization.Id]: Get<
    {},
    { organization: Organization.FromApi; ownStaffProfile: Staff }
  > &
    Patch<
      Partial<Organization.Params>,
      { organization: Organization.FromApi }
    > & {
      "membership-plans": Get<
        { asAdmin?: boolean },
        {
          membershipPlans: Membership.Plan.FromApi[];
          membershipPlanSupplements?: Membership.Plan.Supplement[];
        }
      > &
        Post<
          Membership.Plan.Params.ToApi,
          {
            membershipPlan: Membership.Plan.FromApi;
            membershipPlanSupplement: Membership.Plan.Supplement;
          }
        > & {
          [id: Membership.Plan.Id]: Patch<
            Membership.Plan.Params.ToApi,
            { membershipPlan: Membership.Plan.FromApi }
          > &
            Delete<{}>;
        };
      // Called from organization admin view
      "staffs": Get<{}, { staffs: Staff[] }> & {
        [id: Staff.Id]: Patch<
          Partial<Staff.Params.Organization>,
          { staff: Staff }
        > &
          Delete<{}>;
      };
      "invitations": Get<{}, { invitations: Staff.Invitation[] }> &
        Post<
          Staff.Invitation.Params.Organization,
          { invitation: Staff.Invitation }
        > & {
          [id: Staff.Invitation.Id]: Delete<{}, {}>;
        };
      // Called from user view
      "members": Post<{}, {}>;
      "schedule": Get<
        {
          withSupplements?: boolean;
          bookableTemplateId?: Bookable.Template.Id;
          staffId?: Staff.Id;
          endAtAfter?: Date;
          // 0 indexed
          page?: number;
        },
        Organization.Schedule.WithSupplements
      >;
      "bookables": Get<
        {},
        { bookables: Bookable[]; bookableTemplates: Bookable.Template[] }
      > &
        Post<
          Bookable.Params,
          { bookable: Bookable; bookableSupplement: Bookable.AdminSupplement }
        > & {
          [id: string]: Patch<
            Partial<Bookable.Params> & { reason?: string },
            { bookable: Bookable }
          > &
            Delete<{}> & {
              bookings: {
                [id: string]: Patch<
                  { checkIn?: boolean },
                  {
                    booking: Booking;
                    bookableSupplement: Bookable.AdminSupplement;
                  }
                >;
              };
            };
        };
      "recurrences": Get<{}, { recurrences: Recurrence[] }> &
        Post<
          Recurrence.Params,
          { recurrence: Recurrence; bookables: Bookable[] }
        > & {
          [id: string]: Patch<
            Partial<Recurrence.Params>,
            { recurrence: Recurrence }
          > &
            Delete<{}>;
        };
      "waivers": Get<{}, { waivers: Waiver[]; staffProfiles: Staff[] }> &
        Post<Waiver.Params, { waiver: Waiver }> & {
          [id: string]: Patch<Partial<Waiver.Params>, { waiver: Waiver }>;
        };
      "products": Get<{}, { products: Product[] }> &
        Post<Product.Params, { product: Product }> & {
          [id: string]: Patch<Product.Params.Update, { product: Product }> &
            Delete<{}>;
        };
      "tax-settings": Get<{}, { taxSettings: Organization.TaxSettings }> & {
        [id: string]: Patch<
          { taxSettings: Organization.TaxSettings },
          { taxSettings: Organization.TaxSettings }
        >;
      };
    };
};

const initialState = {
  byId: {} as Record<Organization.Id, Organization>,
  bookablesById: {} as Record<
    Organization.Id,
    Bookable.Id[] & { asAdmin?: boolean }
  >,
  recurrencesById: {} as Record<Organization.Id, Recurrence.Id[]>,
  membershipPlansById: {} as Record<
    Organization.Id,
    Membership.Plan.Id[] & { asAdmin?: boolean }
  >,
  staffsById: {} as Record<Organization.Id, Staff.Id[]>,
  whereUserIs: {} as {
    member?: Organization.Id[];
    staff?: Organization.Id[];
  },
  invitationsById: {} as Record<Organization.Id, Staff.Invitation.Id[]>,
  waiversById: {} as Record<Organization.Id, Waiver.Id[]>,
  productsById: {} as Record<Organization.Id, Product.Id[]>,
  taxSettingsById: {} as Record<Organization.Id, Organization.TaxSettings>,
};

export const { Context, Provider, useOrganizations } = FeatureStore.init(
  "Organizations",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const [bookables, setBookables] = useBookables();
    const [, setBookings] = useBookings();
    const [recurrences, setRecurrences] = useRecurrences();
    const [staffs, setStaffs] = useStaffs();
    const [memberships, setMemberships] = useMemberships();
    const [waivers, setWaivers] = useWaivers();
    const [products, setProducts] = useProducts();

    const accessors = {
      all: attachAction(
        () => Object.values(store.byId),
        () => actions.fetchAll(),
        () => (res) => res.length > 0,
      ),
      byId: Object.assign(
        attachAction(
          (id: Organization.Id) => store.byId[id],
          (id) => actions.fetch(id),
          (id) => (res) => !id || res,
        ),
        {
          bookables: Object.assign(
            attachAction(
              (id: Organization.Id | undefined) =>
                id && store.bookablesById[id]?.map((id) => bookables.byId(id)!),
              (id) => id && actions.fetchSchedule(id),
              (id) => (res) => !id || res,
            ),
            {
              admin: attachAction(
                (id: Organization.Id | undefined) =>
                  id &&
                  (store.bookablesById[id]?.asAdmin || undefined) &&
                  store.bookablesById[id]?.map(
                    (id) => bookables.byId.admin(id)!,
                  ),
                (id) => id && actions.fetchSchedule.asAdmin(id),
                (id) => () => !id || store.bookablesById[id]?.asAdmin,
              ),
            },
          ),
          recurrences: attachAction(
            (id: Organization.Id) =>
              store.recurrencesById[id]?.map((id) => recurrences.byId(id)!),
            (id) => actions.fetchSchedule(id),
            (id) => () => !id || store.recurrencesById[id],
          ),
          membershipPlans: Object.assign(
            attachAction(
              (id: Organization.Id) =>
                store.membershipPlansById[id]?.map(
                  (id) => memberships.planById(id)!,
                ),
              (id) => actions.fetchMembershipPlans(id),
              (id) => (res) => !id || res,
            ),
            {
              admin: attachAction(
                (id: Organization.Id) =>
                  (store.membershipPlansById[id]?.asAdmin || undefined) &&
                  store.membershipPlansById[id]?.map(
                    (id) => memberships.planById.admin(id)!,
                  ),
                (id) => actions.fetchMembershipPlans.asAdmin(id),
                (id) => () => !id || store.membershipPlansById[id]?.asAdmin,
              ),
            },
          ),
          staffs: attachAction(
            (id: Organization.Id | undefined) =>
              id && store.staffsById[id]?.map((id) => staffs.byId(id)!),
            (id) => id && actions.fetchStaffsFor(id),
            (id) => (res) => !id || res,
          ),
          invitations: attachAction(
            (id: Organization.Id | undefined) =>
              id &&
              store.invitationsById[id]?.map((id) => staffs.invitation(id)!),
            (id) => id && actions.fetchInvitationsFor(id),
            (id) => (res) => !id || res,
          ),
          waivers: attachAction(
            (id: Organization.Id) =>
              store.waiversById[id]?.map((id) => waivers.byId(id)!),
            (id) => actions.fetchWaivers(id),
          ),
          generalWaiver: (id: Organization.Id) =>
            accessors.byId
              .waivers(id)
              ?.find((waiver) => waiver.type === "general"),
          membershipTerms: (id: Organization.Id) =>
            accessors.byId
              .waivers(id)
              ?.find((waiver) => waiver.type === "membership"),
          products: attachAction(
            (id: Organization.Id) =>
              store.productsById[id]?.map((id) => products.byId(id)!),
            (id) => actions.fetchProducts(id),
          ),
          taxSettings: attachAction(
            (id: Organization.Id) => store.taxSettingsById[id],
            (id) => actions.fetchTaxSettings(id),
            (id) => (res) => !id || res,
          ),
        },
      ),
      whereUserIs: {
        staff: attachAction(
          () => store.whereUserIs.staff?.map((id) => store.byId[id]!),
          () => actions.fetchForUser(),
        ),
      },
    } as const;

    const mutations = {
      // TODO: Remove after we have generated api contract definitions. This is
      // being done to make api definitions less error prone. If someone forgets
      // to type an organization payload as Organization.FromApi, we won't get
      // type reminders to normalize. Until we get generated definitions, this
      // will make things more fault tolerant.
      one(organization: Organization | Organization.FromApi) {
        setStore(
          "byId",
          organization.id,
          reconcile(Organization.normalize(organization)),
        );
      },
      many(organizations: (Organization | Organization.FromApi)[]) {
        batch(() => {
          for (const org of organizations) {
            mutations.one(org);
          }
        });
      },
      bookables(
        id: Organization.Id,
        bookables: Bookable[] & { asAdmin?: boolean },
      ) {
        setStore(
          "bookablesById",
          id,
          Object.assign(
            bookables
              .sort(Compare.comparator(({ startAt }) => startAt.getTime()))
              .map(({ id }) => id),
            { asAdmin: bookables.asAdmin },
          ),
        );
      },
      addBookables(id: Organization.Id, ...bookables: Bookable[]) {
        const existingBookables = accessors.byId.bookables(id);
        const asAdmin = store.bookablesById[id]?.asAdmin;
        if (!existingBookables)
          throw new Error("Called addBookable before fetching bookables.");
        // Use bookables mutation to retain order
        mutations.bookables(
          id,
          Object.assign([...existingBookables, ...bookables], {
            asAdmin,
          }),
        );
      },
      staffs(id: Organization.Id, staffs: Staff.Id[]) {
        setStore("staffsById", id, staffs);
      },
      addStaff(id: Organization.Id, staff: Staff.Id) {
        setStore("staffsById", id, (staffs) => [...staffs, staff]);
      },
      addOwnStaff(id: Organization.Id) {
        setStore("whereUserIs", "staff", (orgs = []) => [...orgs, id]);
      },
    } as const;

    const actions = wrapActions({
      one: mutations.one,
      many: mutations.many,
      addOwnStaff: mutations.addOwnStaff,
      fetch(id: Organization.Id) {
        return api.organizations[id]!.GET({}).then(
          ({ organization, ownStaffProfile }) => {
            batch(() => {
              mutations.one(Organization.normalize(organization));
              setStaffs.ownForOrg(id, ownStaffProfile);
            });
          },
        );
      },
      fetchAll() {
        return api.organizations.GET({}).then(({ organizations }) => {
          mutations.many(organizations.map(Organization.normalize));
        });
      },
      fetchForUser() {
        return api.organizations
          .GET({ forUser: true })
          .then(({ organizations }) => {
            mutations.many(organizations.map(Organization.normalize));
            setStore(
              "whereUserIs",
              "staff",
              organizations.map(({ id: id }) => id),
            );
          });
      },
      update: (id: Organization.Id, params: Partial<Organization.Params>) =>
        api.organizations[id]!.PATCH(params).then(({ organization }) => {
          mutations.one(Organization.normalize(organization));
        }),
      fetchSchedule: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Organization.Id) =>
              api.organizations[id]!.schedule.GET({
                withSupplements: asAdmin,
              }).then(
                ({
                  bookables,
                  bookableSupplements,
                  staffs,
                  recurrences,
                  bookableTemplates,
                  bookableTemplateSupplements,
                }) => {
                  batch(() => {
                    if (bookableSupplements)
                      setBookables.manySupplements(bookableSupplements);
                    if (bookableTemplateSupplements)
                      setBookables.templates.manySupplements(
                        bookableTemplateSupplements,
                      );
                    setRecurrences.many(recurrences);
                    setBookables.many(bookables);
                    setBookables.templates.many(bookableTemplates);
                    mutations.bookables(
                      id,
                      Object.assign(bookables, { asAdmin }),
                    );
                    setStaffs.many(staffs);
                  });
                },
              ),
        ),
        {
          asAdmin: { asAdmin: true },
        },
      ),
      fetchStaffsFor(id: Organization.Id) {
        return api.organizations[id]!.staffs.GET({}).then(({ staffs }) => {
          batch(() => {
            setStaffs.many(staffs);
            mutations.staffs(
              id,
              staffs.map(({ id }) => id),
            );
          });
        });
      },
      createStaff: (
        id: Organization.Id,
        params: Staff.Invitation.Params.Organization,
      ) =>
        api.organizations[id]!.invitations.POST(params).then(
          ({ invitation }) => {
            batch(() => {
              setStaffs.invitations.one(invitation);
              setStore("invitationsById", id, (invitations = []) =>
                UArray.dedupe([...invitations, invitation.id]),
              );
            });
            return invitation;
          },
        ),
      editStaff: (
        orgId: Organization.Id,
        staffId: Staff.Id,
        role: Staff.Role,
      ) =>
        api.organizations[orgId]!.staffs[staffId]!.PATCH({ role }).then(
          ({ staff }) => {
            setStaffs.one(staff);
            return staff;
          },
        ),
      deleteStaff: (orgId: Organization.Id, staffId: Staff.Id) =>
        api.organizations[orgId]!.staffs[staffId]!.DELETE({}).then(() => {
          batch(() => {
            setStore("staffsById", orgId, (currentStaffs) =>
              currentStaffs.filter((staff) => staff !== staffId),
            );
            setStaffs.remove(staffId);
          });
        }),
      createBookable: (
        id: Organization.Id,
        params: UObject.PartialBy<Bookable.Params, "name">,
      ) =>
        api.organizations[id]!.bookables.POST(params).then(
          ({ bookable, bookableSupplement }) => {
            batch(() => {
              setBookables.one(bookable);
              setBookables.oneSupplement(bookableSupplement);
              mutations.addBookables(id, bookable);
            });
          },
        ),
      editBookable: (
        orgId: Organization.Id,
        bookableId: Bookable.Id,
        params: Partial<Bookable.Params>,
      ) =>
        api.organizations[orgId]!.bookables[bookableId]!.PATCH(params).then(
          ({ bookable }) => {
            setBookables.one(bookable);
            return bookable;
          },
        ),
      deleteBookable: (orgId: Organization.Id, bookableId: Bookable.Id) =>
        api.organizations[orgId]!.bookables[bookableId]!.DELETE({}).then(() => {
          batch(() => {
            setStore("bookablesById", orgId, (currentBookables) =>
              currentBookables.filter((bookable) => bookable !== bookableId),
            );
            setBookables.remove(bookableId);
          });
        }),
      createRecurrence: (orgId: Organization.Id, params: Recurrence.Params) =>
        api.organizations[orgId]!.recurrences.POST(params).then(
          ({ recurrence, bookables }) => {
            batch(() => {
              setRecurrences.one(recurrence);
              setBookables.many(bookables);
              mutations.addBookables(orgId, ...bookables);
            });
            return recurrence;
          },
        ),
      fetchMembershipPlans: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Organization.Id) =>
              api.organizations[id]!["membership-plans"].GET({
                asAdmin,
              }).then(({ membershipPlans, membershipPlanSupplements }) => {
                batch(() => {
                  const activePlans = membershipPlans
                    .filter((plan) => plan.status === "active")
                    .map(Membership.Plan.normalize);
                  setMemberships.manyPlans(activePlans);
                  if (membershipPlanSupplements)
                    setMemberships.manyPlanSupplements(
                      membershipPlanSupplements,
                    );
                  setStore(
                    "membershipPlansById",
                    id,
                    Object.assign(
                      activePlans.map(({ id }) => id),
                      { asAdmin },
                    ),
                  );
                });
              }),
        ),
        { asAdmin: { asAdmin: true } },
      ),
      createMembershipPlan: (
        id: Organization.Id,
        params: Membership.Plan.Params,
      ) =>
        api.organizations[id]!["membership-plans"].POST(
          Membership.Plan.Params.denormalize(params),
        ).then(({ membershipPlan, membershipPlanSupplement }) => {
          const normalized = Membership.Plan.normalize(membershipPlan);
          batch(() => {
            setMemberships.onePlanSupplement(membershipPlanSupplement);
            setStore("membershipPlansById", id, (plans) =>
              Object.assign([...plans, normalized.id], {
                asAdmin: plans.asAdmin,
              }),
            );
            setMemberships.onePlan(normalized);
          });
          return normalized;
        }),
      editMembershipPlan: (
        orgId: Organization.Id,
        membershipPlanId: Membership.Plan.Id,
        updates: Membership.Plan.Params,
      ) => {
        return api.organizations[orgId]!["membership-plans"][
          membershipPlanId
        ]!.PATCH(Membership.Plan.Params.denormalize(updates)).then(
          ({ membershipPlan }) => {
            const normalized = Membership.Plan.normalize(membershipPlan);
            setMemberships.onePlan(normalized);
            return normalized;
          },
        );
      },
      deleteMembershipPlan: (
        orgId: Organization.Id,
        planId: Membership.Plan.Id,
      ) => {
        return api.organizations[orgId]!["membership-plans"][planId]!.DELETE(
          {},
        ).then(() => {
          setStore("membershipPlansById", orgId, (ids) =>
            Object.assign(
              ids.filter((id) => id !== planId),
              { asAdmin: ids.asAdmin },
            ),
          );
        });
      },
      createWaiver: (orgId: Organization.Id, params: Waiver.Params) => {
        return api.organizations[orgId]!.waivers.POST(params).then(
          ({ waiver }) => {
            batch(() => {
              setWaivers.one(waiver);
              setStore("waiversById", orgId, (waivers) => [
                ...waivers,
                waiver.id,
              ]);
            });
            return waiver;
          },
        );
      },
      editWaiver: (
        orgId: Organization.Id,
        waiverId: Waiver.Id,
        params: Partial<Waiver.Params>,
      ) => {
        return api.organizations[orgId]!.waivers[waiverId]!.PATCH(params).then(
          ({ waiver }) => {
            setWaivers.one(waiver);
            return waiver;
          },
        );
      },
      fetchWaivers: (orgId: Organization.Id) => {
        return api.organizations[orgId]!.waivers.GET({}).then(
          ({ waivers, staffProfiles }) => {
            batch(() => {
              setWaivers.many(waivers);
              setStaffs.many(staffProfiles);
              setStore(
                "waiversById",
                orgId,
                waivers.map(({ id }) => id),
              );
            });
          },
        );
      },
      fetchProducts: (orgId: Organization.Id) => {
        return api.organizations[orgId]!.products.GET({}).then(
          ({ products }) => {
            batch(() => {
              setProducts.many(products);
              setStore(
                "productsById",
                orgId,
                products.map(({ id }) => id),
              );
            });
          },
        );
      },
      createProduct: (orgId: Organization.Id, params: Product.Params) => {
        return api.organizations[orgId]!.products.POST(params).then(
          ({ product }) => {
            batch(() => {
              setProducts.one(product);
              setStore("productsById", orgId, (products) => [
                ...products,
                product.id,
              ]);
            });
            return product;
          },
        );
      },
      editProduct: (
        orgId: Organization.Id,
        productId: Product.Id,
        params: Product.Params.Update,
      ) => {
        return api.organizations[orgId]!.products[productId]!.PATCH(
          params,
        ).then(({ product }) => {
          setProducts.one(product);
          return product;
        });
      },
      deleteProduct: (orgId: Organization.Id, productId: Product.Id) => {
        return api.organizations[orgId]!.products[productId]!.DELETE({}).then(
          () => {
            setStore("productsById", orgId, (products) =>
              products.filter((product) => product !== productId),
            );
          },
        );
      },
      updateBooking: (
        orgId: Organization.Id,
        bookableId: Bookable.Id,
        bookingId: Booking.Id,
        params: { checkIn?: boolean },
      ) => {
        return api.organizations[orgId]!.bookables[bookableId]!.bookings[
          bookingId
        ]!.PATCH(params).then(({ booking, bookableSupplement }) => {
          batch(() => {
            setBookings.one(booking);
            setBookables.oneSupplement(bookableSupplement);
          });
        });
      },
      fetchTaxSettings: (orgId: Organization.Id) => {
        return api.organizations[orgId]!["tax-settings"].GET({}).then(
          ({ taxSettings }) => {
            setStore("taxSettingsById", orgId, taxSettings);
          },
        );
      },
      editTaxSettings: (
        orgId: Organization.Id,
        taxSettings: Organization.TaxSettings,
      ) => {
        return api.organizations[orgId]!["tax-settings"]["test"]!.PATCH({
          taxSettings,
        }).then(({ taxSettings }) => {
          setStore("taxSettingsById", orgId, taxSettings);
        });
      },

      fetchInvitationsFor: (orgId: Organization.Id) => {
        return api.organizations[orgId]!.invitations.GET({}).then(
          ({ invitations }) => {
            batch(() => {
              setStaffs.invitations.many(invitations);
              setStore(
                "invitationsById",
                orgId,
                invitations.map(({ id }) => id),
              );
            });
          },
        );
      },
      resendInvitation: (
        orgId: Organization.Id,
        invitationId: Staff.Invitation.Id,
      ) => {
        return api.organizations[orgId]!.invitations.POST(
          staffs.invitation(invitationId)!,
        );
      },
      cancelInvitation: (
        orgId: Organization.Id,
        invitationId: Staff.Invitation.Id,
      ) => {
        return api.organizations[orgId]!.invitations[invitationId]!.DELETE(
          {},
        ).then(() => {
          setStore("invitationsById", orgId, (invitations) =>
            invitations.filter((invitation) => invitation !== invitationId),
          );
        });
      },
    });
    return { accessors, actions };
  },
);
